<template>
  <SlideYUpTransition :duration="animationDuration">
    <div
      class="modal fade"
      :class="[
        inheritedClasses,
        { 'show d-block': show },
        { 'd-none': !show },
        { 'modal-mini': type === 'mini' },
        { 'modal-onboarding': onboarding },
      ]"
      v-show="show"
      tabindex="-1"
      role="dialog"
      :aria-hidden="!show"
      @mousedown.self="handleBackdropClick"
    >
      <div
        class="modal-dialog"
        :class="[
          { 'modal-notice': type === 'notice' },
          { 'modal-dialog-centered': centered },
          modalClasses,
        ]"
      >
        <div
          class="modal-content"
          :class="[
            gradient ? `bg-gradient-${gradient}` : '',
            modalContentClasses,
          ]"
        >
          <!-- ✅ Header siempre visible si hay header o showClose -->
          <div
            class="modal-header"
            :class="[headerClasses]"
            v-if="$slots.header || showClose"
          >
            <slot name="header"></slot>

            <!-- ✅ ÚNICA forma de cerrar -->
            <slot name="close-button">
              <button
                type="button"
                class="close"
                v-if="showClose"
                @click="closeModal"
                aria-label="Close"
              >
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </slot>
          </div>

          <div v-if="$slots.default" class="modal-body" :class="bodyClasses">
            <slot></slot>
          </div>

          <div class="modal-footer" :class="footerClasses" v-if="$slots.footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </div>
  </SlideYUpTransition>
</template>
<script>
import { SlideYUpTransition } from "vue2-transitions";

export default {
  name: "modal",
  components: { SlideYUpTransition },
  props: {
    show: Boolean,
    showClose: { type: Boolean, default: true },
    centered: { type: Boolean, default: false },
    type: { type: String, default: "", validator(v) { return ["", "notice", "mini"].includes(v); } },
    modalClasses: { type: [Object, String] },
    modalContentClasses: { type: [Object, String] },
    gradient: { type: String },
    headerClasses: { type: [Object, String] },
    bodyClasses: { type: [Object, String] },
    footerClasses: { type: [Object, String] },
    animationDuration: { type: Number, default: 500 },
    onboarding: { type: Boolean, default: false },
    closeOnBackdrop: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true },
  },

  data() {
    return {
      _originalParent: null,
      _nextSibling: null,
      _appendedToBody: false,
    };
  },

  mounted() {
    // ✅ Mover el modal al <body> para que position:fixed + z-index funcionen siempre
    this._originalParent = this.$el.parentNode;
    this._nextSibling = this.$el.nextSibling;
    document.body.appendChild(this.$el);
    this._appendedToBody = true;
  },

  methods: {
    closeModal() {
      this.$emit("update:show", false);
      this.$emit("close");
    },
    handleBackdropClick() {
      if (!this.closeOnBackdrop) return;
      this.closeModal();
    },
    handleKeydown(e) {
      if (!this.show) return;
      if (!this.closeOnEsc) return;
      if (e.key === "Escape") this.closeModal();
    },
  },

  watch: {
    show(val) {
      const classes = document.body.classList;
      if (val) {
        classes.add("modal-open");
        document.addEventListener("keydown", this.handleKeydown);
      } else {
        classes.remove("modal-open");
        document.removeEventListener("keydown", this.handleKeydown);
      }
    },
  },

  beforeDestroy() {
    document.body.classList.remove("modal-open");
    document.removeEventListener("keydown", this.handleKeydown);

    // ✅ devolver el nodo a su lugar original (por limpieza)
    if (this._appendedToBody && this._originalParent) {
      if (this._nextSibling) this._originalParent.insertBefore(this.$el, this._nextSibling);
      else this._originalParent.appendChild(this.$el);
    }
  },
};
</script>

<style>
/* ============================================
   Overlay + posicionamiento arriba (default)
   ============================================ */
.modal.show {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;

  /* ✅ Antes estaba en 0 y por eso “se ve el fondo” */
  background-color: rgba(0, 0, 0, 0.35) !important;

  padding-top: 32px !important;
  padding-bottom: 24px !important;

  /* ✅ súbelo un poco para ganarle a sidebar/topbar */
  z-index: 20000 !important;
}

/* Solo modales normales: un poquito más abajo */
.modal:not(.modal-onboarding).show .modal-dialog {
  transform: translateY(24px) !important;
}

/* ============================================
   Tamaño y ancho del modal (default)
   ============================================ */
.modal-dialog {
  margin: 0 auto !important;

  /* ✅ ojo: esto está bien, pero aseguremos que no se “achique” raro */
  width: calc(100% - 32px) !important;
  max-width: 640px !important;

  /* ✅ no fuerces transform aquí; lo controla cada caso */
  transform: none !important;
}

/* ✅ Onboarding: más ancho (ideal para imagen guía) */
.modal.modal-onboarding .modal-dialog {
  max-width: 1100px !important; /* editable */
  width: min(1100px, calc(100% - 32px)) !important; /* ✅ evita que se estreche */
}

/* ============================================
   Contenido (evitar recortes raros)
   ============================================ */
.modal-content {
  max-height: calc(100vh - 80px) !important;

  /* ✅ mejor que auto (solo vertical) */
  overflow: hidden !important;

  border-radius: 14px !important;
  background-clip: padding-box;
}

/* ✅ Onboarding: NO uses overflow: visible (causa recortes/sombras raras) */
.modal.modal-onboarding .modal-content {
  max-height: calc(100vh - 60px) !important;
  overflow: hidden !important;
}

/* ✅ El scroll lo maneja el BODY del modal */
.modal.modal-onboarding .modal-body {
  overflow: auto !important;
  max-height: calc(100vh - 170px) !important; /* header + footer */
}

/* Imagen responsive */
.modal.modal-onboarding .onb-img {
  display: block;
  width: 100% !important;
  height: auto !important;
  object-fit: contain;
}

/* ============================================
   Pantallas pequeñas
   ============================================ */
@media (max-height: 700px) {
  .modal.show {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .modal-content {
    max-height: calc(100vh - 40px) !important;
  }

  .modal.modal-onboarding .modal-content {
    max-height: calc(100vh - 28px) !important;
  }

  .modal.modal-onboarding .modal-body {
    max-height: calc(100vh - 140px) !important;
  }
}

/* Si alguien llega a usar centered=true, no lo dejes bajar */
.modal-dialog-centered {
  align-items: flex-start !important;
}
</style>
